import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, Loader2, Plus, Sparkles, Trash2 } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { Card } from '@orisini/ui/components/card'
import { Input } from '@orisini/ui/components/input'
import { Label } from '@orisini/ui/components/label'
import { Textarea } from '@orisini/ui/components/textarea'
import { useConfirm } from '@orisini/ui/components/confirm'
import { cn } from '@orisini/ui/lib/utils'
import { BRAND_FILES, parseBrandPath } from '@orisini/studio-kit/brand'
import { TARGETS, type Ratio } from '@orisini/studio-kit/ratios'
import type { Workspace } from '../../accounts/lib/accounts'
import { brandApi, diffLines, type BrandFile, type BrandState, type Style } from '../lib/brand'
import { TemplateSection } from './TemplateSection'
import { AudioLibrary } from './AudioLibrary'

/** Rasio preview poster: rasio bawaan platform akun dulu (tanpa akun: 4:5, sama dengan ratioFor di studio), lalu yang lain. */
function posterRatios(productCode: string | null): Ratio[] {
  const utama = productCode === 'pinboss' ? TARGETS.poster.pinterest!.default : TARGETS.poster.instagram!.default
  return [utama, ...(['1:1', '9:16'] as Ratio[]).filter((r) => r !== utama)]
}

const PLACEHOLDER: Record<string, string> = {
  'BRAND.md': 'e.g. We are a calm tech news account for busy founders. Use navy and off-white with a lime accent.',
  'POSTER.md': 'e.g. Put the headline at the bottom over a dark gradient on the photo. Make it bolder.',
  'CAROUSEL.md': 'e.g. Use a dark background, big typography, and yellow to highlight key numbers.',
  'VIDEO.md': 'e.g. Fast cuts, captions at the centre, a hook question in the first second, and a soft whoosh on every scene change.',
  'WRITING.md': 'e.g. Write in casual Indonesian, short sentences, one emoji at most.',
}

/**
 * Brand & Styles: MD per akun tampil sebagai kategori, bukan berkas. Isinya
 * diubah lewat "Customize with AI" (perintah -> diff -> Apply); editor
 * Markdown mentah hanya di bawah Advanced.
 */
export function BrandTab({ workspace: w }: { workspace: Workspace }) {
  const [params, setParams] = useSearchParams()
  const path = params.get('file')
  const [state, setState] = useState<BrandState | null>(null)

  const muat = useCallback(async () => {
    try {
      setState(await brandApi.load(w.id))
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't load brand settings")
    }
  }, [w.id])

  useEffect(() => void muat(), [muat])

  const buka = (p: string | null) => {
    const next = new URLSearchParams(params)
    if (p) next.set('file', p)
    else next.delete('file')
    setParams(next)
  }

  if (!state) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading…
      </div>
    )
  }

  const file = path ? state.files.find((f) => f.path === path) : null
  if (path && file) return <FileDetail workspace={w} state={state} file={file} onOpen={buka} onChanged={muat} />

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        These rules decide how this project's posts look and sound. Describe what you want in plain words — Orisini rewrites the rules and rebuilds the designs.
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {BRAND_FILES.map((info) => {
          const f = state.files.find((x) => x.path === info.path)
          const styles = info.styles ? state.styles.filter((s) => s.kind === info.styles) : []
          const tpl =
            info.path === 'POSTER.md'
              ? [state.templates.find((t) => t.kind === 'poster')!]
              : info.path === 'CAROUSEL.md'
                ? state.templates.filter((t) => t.kind === 'carousel')
                : info.path === 'VIDEO.md'
                  ? state.templates.filter((t) => t.kind === 'video')
                  : []
          const perluBangun = tpl.some((t) => t && t.source === 'compiled' && !t.upToDate)
          return (
            <button
              key={info.path}
              type="button"
              onClick={() => buka(info.path)}
              className="flex flex-col gap-2 rounded-lg border border-border p-4 text-left transition-colors hover:border-foreground/40"
            >
              <span className="flex items-center justify-between gap-2">
                <span className="font-medium">{info.title}</span>
                <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide', f?.source === 'starter' ? 'bg-muted text-muted-foreground' : 'bg-emerald-500/15 text-emerald-400')}>
                  {f?.source === 'starter' ? 'Starter' : 'Customised'}
                </span>
              </span>
              <span className="text-xs text-muted-foreground">{info.description}</span>
              <span className="mt-auto text-xs text-muted-foreground">
                {[
                  info.styles ? `${styles.length} ${styles.length === 1 ? 'style' : 'styles'}` : null,
                  perluBangun ? 'Design needs a rebuild' : null,
                ]
                  .filter(Boolean)
                  .join(' · ') || ' '}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function FileDetail({
  workspace: w,
  state,
  file,
  onOpen,
  onChanged,
}: {
  workspace: Workspace
  state: BrandState
  file: BrandFile
  onOpen: (path: string | null) => void
  onChanged: () => Promise<void>
}) {
  const parsed = parseBrandPath(file.path)
  const style = parsed && 'style' in parsed ? state.styles.find((s) => s.kind === parsed.style && s.slug === parsed.slug) : null
  const info = BRAND_FILES.find((b) => b.path === file.path)
  const judul = info?.title ?? `${style?.name ?? ''} style`
  const induk = style ? (style.kind === 'carousel' ? 'CAROUSEL.md' : 'VIDEO.md') : null

  const templatePoster = file.path === 'POSTER.md' ? state.templates.find((t) => t.kind === 'poster') : undefined
  const templateStyle = style ? state.templates.find((t) => t.kind === style.kind && t.style === style.slug) : undefined
  const terpengaruh =
    file.path === 'BRAND.md'
      ? state.templates
      : file.path === 'CAROUSEL.md'
        ? state.templates.filter((t) => t.kind === 'carousel')
        : file.path === 'VIDEO.md'
          ? state.templates.filter((t) => t.kind === 'video')
          : []
  const usang = terpengaruh.filter((t) => t.source === 'compiled' && !t.upToDate)

  return (
    <div className="space-y-6">
      <button type="button" onClick={() => onOpen(induk)} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> {induk ? BRAND_FILES.find((b) => b.path === induk)!.title : 'Brand & Styles'}
      </button>
      <div>
        <h2 className="text-lg font-semibold">{judul}</h2>
        <p className="text-sm text-muted-foreground">{info?.description ?? `What makes the "${style?.name}" ${style?.kind} style different.`}</p>
      </div>

      <Customize workspaceId={w.id} file={file} afterApply={async () => {
        await onChanged()
        // Berkas yang menjadi bahan satu template langsung dibangun ulang;
        // BRAND.md/CAROUSEL.md memengaruhi banyak template, jadi ditawarkan saja.
        if (file.path === 'POSTER.md' || templateStyle) {
          try {
            const r = await brandApi.compile(w.id, templateStyle ? style!.kind : 'poster', templateStyle ? style!.slug : undefined)
            toast[r.ok ? 'success' : 'error'](r.ok ? 'Design rebuilt with the new rules' : "The new design didn't pass our checks; the previous one is kept")
          } catch (err) {
            toast.info(err instanceof Error ? err.message : 'Rules saved. Rebuild the design when AI is available.')
          }
          await onChanged()
        }
      }} />

      {usang.length > 0 && (
        <Card className="flex flex-wrap items-center gap-3 border-amber-500/30 p-4 text-sm">
          <p className="min-w-0 flex-1">
            {usang.length} {usang.length === 1 ? 'design was' : 'designs were'} built from older rules. Open them to rebuild.
          </p>
          <div className="flex flex-wrap gap-2">
            {usang.map((t) => (
              <Button key={`${t.kind}:${t.style}`} size="sm" variant="outline" onClick={() => onOpen(t.kind === 'poster' ? 'POSTER.md' : `${t.kind}/${t.style}.md`)}>
                {t.kind === 'poster' ? 'Poster' : state.styles.find((s) => s.slug === t.style)?.name ?? t.style}
              </Button>
            ))}
          </div>
        </Card>
      )}

      {templatePoster && <TemplateSection workspaceId={w.id} info={templatePoster} ratios={posterRatios(w.productCode)} onChanged={onChanged} />}
      {templateStyle && <TemplateSection workspaceId={w.id} info={templateStyle} ratios={templateStyle.kind === 'video' ? ['9:16'] : ['4:5']} onChanged={onChanged} />}

      {info?.styles && <StyleList workspaceId={w.id} kind={info.styles} styles={state.styles.filter((s) => s.kind === info.styles)} onOpen={onOpen} onChanged={onChanged} />}
      {style && <StyleActions workspaceId={w.id} style={style} onDeleted={async () => {
        await onChanged()
        onOpen(induk)
      }} onChanged={onChanged} />}
      {file.path === 'VIDEO.md' &&<AudioLibrary workspaceId={w.id} />}

      <Advanced workspaceId={w.id} file={file} maxLength={state.maxLength} onSaved={onChanged} />
    </div>
  )
}

function Customize({ workspaceId, file, afterApply }: { workspaceId: string; file: BrandFile; afterApply: () => Promise<void> }) {
  const [instruction, setInstruction] = useState('')
  const [usulan, setUsulan] = useState<{ content: string; summary: string | null } | null>(null)
  const [busy, setBusy] = useState<'propose' | 'apply' | null>(null)

  const usul = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy('propose')
    try {
      setUsulan(await brandApi.propose(workspaceId, file.path, instruction))
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't generate changes")
    } finally {
      setBusy(null)
    }
  }

  const terapkan = async () => {
    if (!usulan) return
    setBusy('apply')
    try {
      await brandApi.save(workspaceId, file.path, usulan.content, instruction)
      toast.success('Rules updated')
      setUsulan(null)
      setInstruction('')
      await afterApply()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save the changes")
    } finally {
      setBusy(null)
    }
  }

  const diff = usulan ? diffLines(file.content, usulan.content) : []
  const berubah = diff.some((d) => d.op !== 'same')

  return (
    <Card className="space-y-3 p-4">
      <form onSubmit={usul} className="space-y-2">
        <Label htmlFor="customize" className="flex items-center gap-1.5">
          <Sparkles className="size-4" /> Customize with AI
        </Label>
        <Textarea
          id="customize"
          rows={3}
          maxLength={2000}
          value={instruction}
          disabled={busy !== null || usulan !== null}
          placeholder={PLACEHOLDER[file.path] ?? 'Describe how this style should look and what makes it different.'}
          onChange={(e) => setInstruction(e.target.value)}
        />
        {!usulan && (
          <Button type="submit" size="sm" disabled={busy !== null || !instruction.trim()}>
            {busy === 'propose' && <Loader2 className="animate-spin" />}
            {busy === 'propose' ? 'Writing…' : 'Generate changes'}
          </Button>
        )}
      </form>

      {usulan && (
        <div className="space-y-3">
          {usulan.summary && <p className="text-sm">{usulan.summary}</p>}
          {berubah ? (
            <pre className="max-h-80 overflow-auto rounded-md border border-border bg-muted/40 p-3 text-xs leading-relaxed">
              {diff.map((d, i) => (
                <div key={i} className={d.op === 'add' ? 'bg-emerald-500/15 text-emerald-300' : d.op === 'del' ? 'bg-destructive/15 text-destructive line-through' : 'text-muted-foreground'}>
                  {d.op === 'add' ? '+ ' : d.op === 'del' ? '- ' : '  '}
                  {d.text || ' '}
                </div>
              ))}
            </pre>
          ) : (
            <p className="text-sm text-muted-foreground">The AI didn't change anything. Try describing the change differently.</p>
          )}
          <div className="flex gap-2">
            <Button size="sm" disabled={busy !== null || !berubah} onClick={terapkan}>
              {busy === 'apply' && <Loader2 className="animate-spin" />}
              Apply
            </Button>
            <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => setUsulan(null)}>
              Discard
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}

function Advanced({ workspaceId, file, maxLength, onSaved }: { workspaceId: string; file: BrandFile; maxLength: number; onSaved: () => Promise<void> }) {
  const [isi, setIsi] = useState(file.content)
  const [saving, setSaving] = useState(false)
  useEffect(() => setIsi(file.content), [file.content])

  const simpan = async () => {
    setSaving(true)
    try {
      await brandApi.save(workspaceId, file.path, isi)
      toast.success('File saved')
      await onSaved()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save the file")
    } finally {
      setSaving(false)
    }
  }

  return (
    <details className="rounded-lg border border-border">
      <summary className="cursor-pointer px-4 py-3 text-sm text-muted-foreground">
        Advanced · edit <code className="text-xs">{file.path}</code> directly (version {file.version})
      </summary>
      <div className="space-y-2 border-t border-border p-4">
        <Textarea value={isi} rows={16} maxLength={maxLength} className="font-mono text-xs" onChange={(e) => setIsi(e.target.value)} />
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            {isi.length}/{maxLength}
          </span>
          <Button size="sm" variant="outline" disabled={saving || isi === file.content || !isi.trim()} onClick={simpan}>
            {saving && <Loader2 className="animate-spin" />}
            Save file
          </Button>
        </div>
      </div>
    </details>
  )
}

function StyleList({
  workspaceId,
  kind,
  styles,
  onOpen,
  onChanged,
}: {
  workspaceId: string
  kind: 'carousel' | 'video'
  styles: Style[]
  onOpen: (path: string) => void
  onChanged: () => Promise<void>
}) {
  const [form, setForm] = useState<{ name: string; description: string } | null>(null)
  const [saving, setSaving] = useState(false)

  const buat = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form) return
    setSaving(true)
    try {
      const r = await brandApi.createStyle(workspaceId, kind, form.name, form.description)
      toast.success(r.aiWrote ? `${r.style.name} created` : `${r.style.name} created from your description — refine it with AI later`)
      setForm(null)
      await onChanged()
      onOpen(r.style.path)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't create the style")
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold">{kind === 'carousel' ? 'Carousel styles' : 'Video styles'}</h3>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {styles.map((s) => (
          <button
            key={s.slug}
            type="button"
            onClick={() => onOpen(s.path)}
            className="flex flex-col gap-1 rounded-lg border border-border p-4 text-left transition-colors hover:border-foreground/40"
          >
            <span className="flex items-center justify-between gap-2 font-medium">
              {s.name}
              {!s.active && <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">Off</span>}
            </span>
            <code className="text-xs text-muted-foreground">{s.path}</code>
          </button>
        ))}
        {!form && (
          <button
            type="button"
            onClick={() => setForm({ name: '', description: '' })}
            className="flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground"
          >
            <Plus className="size-4" /> Create new style
          </button>
        )}
      </div>
      {form && (
        <Card className="p-4">
          <form onSubmit={buat} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="style-name">Style name</Label>
              <Input id="style-name" value={form.name} maxLength={40} autoFocus placeholder={kind === 'carousel' ? 'Versus' : 'Explainer'} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="style-desc">Describe it</Label>
              <Textarea
                id="style-desc"
                rows={3}
                maxLength={2000}
                value={form.description}
                placeholder={kind === 'carousel' ? 'Two options side by side on every slide, with a clear winner at the end.' : 'Calm narration over slow photo zooms, one key number per scene.'}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">Orisini AI turns this into the style's rules.</p>
            </div>
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={saving || !form.name.trim()}>
                {saving && <Loader2 className="animate-spin" />}
                {saving ? 'Creating…' : 'Create style'}
              </Button>
              <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={() => setForm(null)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}
    </section>
  )
}

function StyleActions({ workspaceId, style, onDeleted, onChanged }: { workspaceId: string; style: Style; onDeleted: () => Promise<void>; onChanged: () => Promise<void> }) {
  const confirm = useConfirm()
  const [busy, setBusy] = useState(false)

  const hapus = async () => {
    const ok = await confirm({
      title: `Delete the "${style.name}" style?`,
      description: 'Its rules and design are removed. Posts already made with it are not affected.',
      confirmLabel: 'Delete',
      destructive: true,
    })
    if (!ok) return
    setBusy(true)
    try {
      await brandApi.deleteStyle(workspaceId, style.kind, style.slug)
      toast.success(`${style.name} deleted`)
      await onDeleted()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't delete the style")
      setBusy(false)
    }
  }

  const toggle = async () => {
    setBusy(true)
    try {
      await brandApi.updateStyle(workspaceId, style.kind, style.slug, { active: !style.active })
      await onChanged()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't update the style")
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="flex flex-wrap items-center gap-3 p-4">
      <p className="min-w-0 flex-1 text-sm">
        {style.active ? 'This style can be picked for new posts.' : 'This style is off — it is never picked for new posts.'}
      </p>
      <Button size="sm" variant="outline" disabled={busy} onClick={toggle}>
        {style.active ? 'Turn off' : 'Turn on'}
      </Button>
      <Button size="sm" variant="ghost" disabled={busy} onClick={hapus} aria-label="Delete style" title="Delete style">
        <Trash2 />
      </Button>
    </Card>
  )
}
